import { isSeededListing } from "@/lib/seededListings";

const DEFAULT_LOW_STOCK_THRESHOLD = 5;
export const MAX_ORDER_QUANTITY = 5;
export const INVENTORY_MODES = {
  FIXED_STOCK: "fixed_stock",
  DAILY_CAPACITY: "daily_capacity",
  ONE_OF_A_KIND: "one_of_a_kind",
  UNLIMITED: "unlimited",
};

export function getLowStockThreshold(listing) {
  const raw = listing?.low_stock_threshold;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return DEFAULT_LOW_STOCK_THRESHOLD;
}

export function normalizeInventory(listing) {
  if (isSeededListing(listing)) {
    return { availability: "preview", label: "Coming soon", statusKey: "seeded_preview" };
  }

  const inventoryMode = normalizeInventoryMode(listing?.inventory_mode);
  const statusKey = normalizeInventoryStatus(listing?.inventory_status);

  if (statusKey === "out_of_stock") {
    return { availability: "out", label: "Unavailable", statusKey };
  }

  if (inventoryMode === INVENTORY_MODES.UNLIMITED) {
    return { availability: "available", label: "Available", statusKey: "unlimited" };
  }

  if (inventoryMode === INVENTORY_MODES.DAILY_CAPACITY) {
    return normalizeDailyCapacityInventory(listing);
  }

  if (inventoryMode === INVENTORY_MODES.ONE_OF_A_KIND) {
    const quantity = getAvailableInventoryQuantity(listing);
    if (quantity === null || quantity <= 0) {
      return { availability: "out", label: "Sold", statusKey };
    }
    return { availability: "available", label: "One-of-a-kind", statusKey };
  }

  const quantityRaw = listing?.inventory_quantity;
  const quantity = quantityRaw === null || quantityRaw === undefined
    ? null
    : Number(quantityRaw);
  const threshold = getLowStockThreshold(listing);

  if (quantity === null || Number.isNaN(quantity)) {
    return { availability: "out", label: "Unavailable", statusKey };
  }

  if (quantity <= 0) {
    return { availability: "out", label: "Out of stock", statusKey };
  }

  if (quantity <= threshold) {
    return { availability: "low", label: "Low stock", statusKey };
  }

  return { availability: "available", label: "Available", statusKey };
}

export function normalizeInventoryStatus(value) {
  const status = String(value || "in_stock").trim().toLowerCase();
  if (status === "out_of_stock" || status === "seasonal" || status === "temporary") {
    return "out_of_stock";
  }
  return "in_stock";
}

export function normalizeInventoryMode(value) {
  const mode = String(value || "").trim().toLowerCase();
  if (Object.values(INVENTORY_MODES).includes(mode)) return mode;
  return INVENTORY_MODES.FIXED_STOCK;
}

function toNonNegativeIntegerOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.trunc(parsed));
}

function getLocalDateParts(date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

function formatDateOnly(date) {
  const { year, month, day } = getLocalDateParts(date);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function startOfLocalDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function parseDateOnly(value) {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(parsed.getTime())) return null;
  if (formatDateOnly(parsed) !== `${match[1]}-${match[2]}-${match[3]}`) return null;
  return parsed;
}

function getMaxDaysAhead(listing) {
  const parsed = toNonNegativeIntegerOrNull(listing?.max_days_ahead);
  if (parsed !== null && parsed > 0) return Math.min(parsed, 7);
  return 7;
}

function parseTimeToMinutes(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function formatTimeForPickupLabel(value) {
  const minutes = parseTimeToMinutes(value);
  if (minutes === null) return null;
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const hour12 = hour24 % 12 || 12;
  const period = hour24 >= 12 ? "PM" : "AM";
  return minute === 0
    ? `${hour12} ${period}`
    : `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

export function resolveBetaFulfillmentDate(listing, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const sameDayEnabled = listing?.same_day_enabled !== false;
  const cutoffMinutes = parseTimeToMinutes(listing?.order_cutoff_time);
  const minLeadMinutes = toNonNegativeIntegerOrNull(listing?.min_lead_minutes) ?? 120;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const leadTarget = new Date(now.getTime() + minLeadMinutes * 60 * 1000);
  const leadSameCalendarDay = formatDateOnly(leadTarget) === formatDateOnly(now);
  const beforeCutoff = cutoffMinutes === null || currentMinutes < cutoffMinutes;
  const todayEligible = sameDayEnabled && beforeCutoff && leadSameCalendarDay;

  return {
    date: formatDateOnly(todayEligible ? now : addDays(now, 1)),
    label: todayEligible ? "today" : "tomorrow",
    isToday: todayEligible,
  };
}

export function getDailyCapacityPickupDateWindow(listing, options = {}) {
  const now = options.now instanceof Date ? options.now : new Date();
  const earliest = resolveBetaFulfillmentDate(listing, { now }).date;
  const maxDaysAhead = getMaxDaysAhead(listing);
  const start = startOfLocalDay(now);
  const end = addDays(start, maxDaysAhead - 1);

  return {
    earliestDate: earliest,
    latestDate: formatDateOnly(end),
    maxDaysAhead,
  };
}

export function validateDailyCapacityFulfillmentDate(listing, selectedDate, options = {}) {
  const mode = normalizeInventoryMode(listing?.inventory_mode);
  if (mode !== INVENTORY_MODES.DAILY_CAPACITY) {
    return { ok: true, date: null, reason: "not_daily_capacity" };
  }

  const fallback = resolveBetaFulfillmentDate(listing, options);
  const date = selectedDate ? parseDateOnly(selectedDate) : parseDateOnly(fallback.date);
  if (!date) {
    return {
      ok: false,
      date: null,
      reason: "invalid_date",
      message: "Choose a valid pickup date.",
    };
  }

  const key = formatDateOnly(date);
  const window = getDailyCapacityPickupDateWindow(listing, options);
  if (key < window.earliestDate) {
    return {
      ok: false,
      date: key,
      reason: "before_earliest",
      message: "Choose a pickup date that is still available.",
    };
  }
  if (key > window.latestDate) {
    return {
      ok: false,
      date: key,
      reason: "outside_window",
      message: "Choose a pickup date within the available pickup window.",
    };
  }

  return { ok: true, date: key, reason: null, fallbackDate: fallback.date };
}

export function formatPickupDateOptionLabel(value) {
  const parsed = parseDateOnly(value);
  if (!parsed) return "";
  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function getDailyCapacityDisplayDateLabel(value, options = {}) {
  const parsed = parseDateOnly(value);
  if (!parsed) return null;
  const now = options.now instanceof Date ? options.now : new Date();
  const todayKey = formatDateOnly(now);
  const tomorrowKey = formatDateOnly(addDays(now, 1));
  const key = formatDateOnly(parsed);
  if (key === todayKey) return "today";
  if (key === tomorrowKey) return "tomorrow";
  return formatPickupDateOptionLabel(key);
}

/**
 * @param {{ listing?: Record<string, unknown>, capacityOverrides?: Array<Record<string, unknown>>, completedOrderItems?: Array<Record<string, unknown>>, activeReservations?: Array<Record<string, unknown>>, now?: Date }} options
 */
export function buildDailyCapacityPickupDateOptions({
  listing,
  capacityOverrides = [],
  completedOrderItems = [],
  activeReservations = [],
  now = new Date(),
} = {}) {
  if (normalizeInventoryMode(listing?.inventory_mode) !== INVENTORY_MODES.DAILY_CAPACITY) {
    return [];
  }

  const window = getDailyCapacityPickupDateWindow(listing, { now });
  const start = startOfLocalDay(now);
  const options = [];

  for (let index = 0; index < window.maxDaysAhead; index += 1) {
    const date = formatDateOnly(addDays(start, index));
    const dateValidation = validateDailyCapacityFulfillmentDate(listing, date, { now });
    if (!dateValidation.ok) continue;

    const availability = calculateInventoryAvailability({
      listing,
      fulfillmentDate: date,
      capacityOverrides,
      completedOrderItems,
      activeReservations,
      now,
    });
    const availableQuantity = Math.max(0, Number(availability.availableQuantity || 0));
    if (availableQuantity <= 0) continue;

    options.push({
      date,
      label: formatPickupDateOptionLabel(date),
      availableQuantity,
    });
  }

  return options;
}

export function resolveDailyCapacityForDate({ listing, capacityDate, capacityOverrides = [] }) {
  const override = (Array.isArray(capacityOverrides) ? capacityOverrides : []).find(
    (row) =>
      String(row?.listing_id || row?.listingId || listing?.id || "") ===
        String(listing?.id || row?.listing_id || row?.listingId || "") &&
      String(row?.capacity_date || row?.capacityDate || "") === String(capacityDate || "")
  );

  if (override?.is_closed === true) return 0;
  if (override && override.capacity_limit !== undefined) {
    return toNonNegativeIntegerOrNull(override.capacity_limit);
  }
  return toNonNegativeIntegerOrNull(listing?.daily_capacity);
}

export function calculateInventoryAvailability({
  listing,
  fulfillmentDate = null,
  capacityOverrides = [],
  completedOrderItems = [],
  activeReservations = [],
  now = new Date(),
}) {
  const mode = normalizeInventoryMode(listing?.inventory_mode);
  if (mode === INVENTORY_MODES.UNLIMITED) {
    return { mode, availableQuantity: null, capacity: null, soldQuantity: 0, reservedQuantity: 0 };
  }

  const resolvedDate =
    mode === INVENTORY_MODES.DAILY_CAPACITY
      ? fulfillmentDate || resolveBetaFulfillmentDate(listing, { now }).date
      : null;
  const capacity =
    mode === INVENTORY_MODES.DAILY_CAPACITY
      ? resolveDailyCapacityForDate({ listing, capacityDate: resolvedDate, capacityOverrides })
      : mode === INVENTORY_MODES.ONE_OF_A_KIND
        ? getAvailableInventoryQuantity(listing)
      : getAvailableInventoryQuantity(listing);

  const soldQuantity = (Array.isArray(completedOrderItems) ? completedOrderItems : []).reduce(
    (sum, item) => {
      if (String(item?.listing_id || "") !== String(listing?.id || "")) return sum;
      if (mode === INVENTORY_MODES.DAILY_CAPACITY) {
        if (String(item?.fulfillment_date || "") !== String(resolvedDate || "")) return sum;
      }
      return sum + Math.max(0, Number(item?.quantity || 0));
    },
    0
  );

  const nowMs = now.getTime();
  const reservedQuantity = (Array.isArray(activeReservations) ? activeReservations : []).reduce(
    (sum, reservation) => {
      if (String(reservation?.listing_id || "") !== String(listing?.id || "")) return sum;
      if (reservation?.status && reservation.status !== "active") return sum;
      if (reservation?.expires_at && Date.parse(reservation.expires_at) <= nowMs) return sum;
      if (mode === INVENTORY_MODES.DAILY_CAPACITY) {
        if (String(reservation?.reservation_date || "") !== String(resolvedDate || "")) return sum;
      }
      return sum + Math.max(0, Number(reservation?.quantity || reservation?.reserved_quantity || 0));
    },
    0
  );

  const availableQuantity =
    capacity === null ? 0 : Math.max(0, Number(capacity || 0) - soldQuantity - reservedQuantity);

  return {
    mode,
    fulfillmentDate: resolvedDate,
    availableQuantity,
    capacity,
    soldQuantity,
    reservedQuantity,
  };
}

function isListingGenerallyUnavailable(listing) {
  if (isSeededListing(listing)) return { unavailable: true, reason: "preview" };
  if (normalizeInventoryStatus(listing?.inventory_status) === "out_of_stock") {
    return { unavailable: true, reason: "inventory_status" };
  }
  const status = String(listing?.status || "").trim().toLowerCase();
  if (status && !["published", "live", "available", "active"].includes(status)) {
    return { unavailable: true, reason: "listing_status" };
  }
  if (listing?.deleted_at) return { unavailable: true, reason: "deleted" };
  if (listing?.admin_hidden === true || listing?.is_admin_hidden === true) {
    return { unavailable: true, reason: "admin_hidden" };
  }
  return { unavailable: false, reason: null };
}

export function getEffectiveListingAvailability(listing, options = {}) {
  const unavailable = isListingGenerallyUnavailable(listing);
  const mode = normalizeInventoryMode(listing?.inventory_mode);

  if (unavailable.unavailable) {
    return {
      isAvailable: false,
      availableQuantity: 0,
      availabilityLabel: "Currently unavailable",
      availableDateLabel: null,
      pickupCardLabel: null,
      reason: unavailable.reason,
      inventoryMode: mode,
    };
  }

  if (mode === INVENTORY_MODES.DAILY_CAPACITY) {
    const resolved = resolveBetaFulfillmentDate(listing, { now: options.now });
    const pickupDate = options.pickupDate || resolved.date;
    const availability = calculateInventoryAvailability({
      listing,
      fulfillmentDate: pickupDate,
      capacityOverrides: options.capacityOverrides,
      completedOrderItems: options.completedOrderItems,
      activeReservations: options.activeReservations,
      now: options.now,
    });
    const availableQuantity = Math.max(0, Number(availability.availableQuantity || 0));
    const labelDate =
      getDailyCapacityDisplayDateLabel(pickupDate, { now: options.now }) ||
      (resolved.isToday ? "today" : "tomorrow");

    if (availability.capacity === null) {
      return {
        isAvailable: false,
        availableQuantity: 0,
        availabilityLabel: "Currently unavailable",
        availableDateLabel: null,
        pickupCardLabel: null,
        reason: "missing_daily_capacity",
        inventoryMode: mode,
        fulfillmentDate: pickupDate,
      };
    }

    if (availableQuantity <= 0) {
      return {
        isAvailable: false,
        availableQuantity: 0,
        availabilityLabel: `Sold out ${labelDate}`,
        availableDateLabel: labelDate,
        pickupCardLabel: null,
        reason: "sold_out",
        inventoryMode: mode,
        fulfillmentDate: pickupDate,
      };
    }

    const cutoffLabel = formatTimeForPickupLabel(listing?.order_cutoff_time);
    const pickupCardLabel =
      labelDate === "today"
        ? cutoffLabel
          ? `Pickup today until ${cutoffLabel}`
          : "Pickup today"
        : labelDate === "tomorrow"
          ? "Pickup tomorrow"
          : `Pickup ${labelDate}`;

    return {
      isAvailable: true,
      availableQuantity,
      availabilityLabel: `Available ${labelDate}`,
      availableDateLabel: labelDate,
      pickupCardLabel,
      reason: resolved.isToday || labelDate !== "today" ? null : "same_day_unavailable",
      inventoryMode: mode,
      fulfillmentDate: pickupDate,
    };
  }

  const inventory = normalizeInventory(listing);
  const availableQuantity =
    mode === INVENTORY_MODES.UNLIMITED
      ? MAX_ORDER_QUANTITY
      : getAvailableInventoryQuantity(listing);
  const normalizedAvailable =
    availableQuantity === null ? 0 : Math.max(0, Number(availableQuantity || 0));

  return {
    isAvailable: inventory.availability !== "out" && normalizedAvailable > 0,
    availableQuantity: normalizedAvailable,
    availabilityLabel:
      inventory.availability === "out" ? inventory.label || "Out of stock" : inventory.label || "Available",
    availableDateLabel: null,
    pickupCardLabel: null,
    reason: inventory.availability === "out" ? "out_of_stock" : null,
    inventoryMode: mode,
  };
}

function normalizeDailyCapacityInventory(listing) {
  const effective = getEffectiveListingAvailability(listing);
  const capacity = effective.availableQuantity;
  const threshold = getLowStockThreshold(listing);

  if (effective.reason === "missing_daily_capacity") {
    return { availability: "out", label: "Unavailable", statusKey: "daily_capacity" };
  }

  if (capacity <= 0) {
    return {
      availability: "out",
      label: effective.availabilityLabel,
      statusKey: "daily_capacity",
    };
  }

  if (effective.availabilityLabel === "Available tomorrow") {
    return { availability: "available", label: "Available tomorrow", statusKey: "daily_capacity" };
  }

  if (capacity <= threshold) {
    return {
      availability: "low",
      label: `Only ${capacity} left today`,
      statusKey: "daily_capacity",
    };
  }

  return { availability: "available", label: "Available today", statusKey: "daily_capacity" };
}

export function getAvailableInventoryQuantity(listing) {
  if (isSeededListing(listing)) return 0;
  const inventoryMode = normalizeInventoryMode(listing?.inventory_mode);
  if (inventoryMode === INVENTORY_MODES.UNLIMITED) return MAX_ORDER_QUANTITY;
  if (inventoryMode === INVENTORY_MODES.DAILY_CAPACITY) {
    return getEffectiveListingAvailability(listing).availableQuantity;
  }
  if (inventoryMode === INVENTORY_MODES.ONE_OF_A_KIND) {
    const statusKey = String(listing?.inventory_status || "in_stock").trim();
    if (statusKey === "out_of_stock") return 0;
    const quantityRaw = listing?.inventory_quantity;
    if (quantityRaw === null || quantityRaw === undefined || quantityRaw === "") return null;
    const quantity = Number(quantityRaw);
    if (!Number.isFinite(quantity)) return null;
    return quantity > 0 ? 1 : 0;
  }

  const statusKey = String(listing?.inventory_status || "in_stock").trim();
  if (statusKey === "out_of_stock") return 0;

  const quantityRaw = listing?.inventory_quantity;
  if (quantityRaw === null || quantityRaw === undefined || quantityRaw === "") {
    // YourBarrio checkout treats NULL inventory as not tracked and not sellable.
    // Listings need a concrete inventory_quantity before the reservation RPC can sell them.
    return null;
  }

  const quantity = Number(quantityRaw);
  if (!Number.isFinite(quantity)) return null;
  return Math.max(0, Math.floor(quantity));
}

export function getMaxPurchasableQuantity(listing) {
  const mode = normalizeInventoryMode(listing?.inventory_mode);
  if (mode === INVENTORY_MODES.DAILY_CAPACITY) {
    return Math.max(0, Number(getEffectiveListingAvailability(listing).availableQuantity || 0));
  }
  if (mode === INVENTORY_MODES.ONE_OF_A_KIND) {
    const available = getAvailableInventoryQuantity(listing);
    if (available === null) return 0;
    return available > 0 ? 1 : 0;
  }
  const available = getAvailableInventoryQuantity(listing);
  if (available === null) return 0;
  return Math.max(0, Math.min(MAX_ORDER_QUANTITY, available));
}

export function clampOrderQuantity(quantity, listing) {
  const parsed = Number(quantity);
  const requested = Number.isFinite(parsed) ? Math.round(parsed) : 1;
  const maxQuantity = getMaxPurchasableQuantity(listing);
  if (maxQuantity <= 0) return 0;
  return Math.max(1, Math.min(maxQuantity, requested));
}

export function validateOrderQuantity(quantity, listing) {
  const parsed = Number(quantity);
  if (!Number.isFinite(parsed) || Math.round(parsed) !== parsed || parsed < 1) {
    return {
      ok: false,
      code: "INVALID_QUANTITY",
      message: "Choose at least 1 item.",
      maxQuantity: getMaxPurchasableQuantity(listing),
    };
  }

  const maxQuantity = getMaxPurchasableQuantity(listing);
  const mode = normalizeInventoryMode(listing?.inventory_mode);
  if (mode === INVENTORY_MODES.ONE_OF_A_KIND && parsed > 1) {
    return {
      ok: false,
      code: "ONE_OF_A_KIND_QUANTITY_EXCEEDED",
      message: "Only one is available for this item.",
      maxQuantity,
    };
  }
  if (mode !== INVENTORY_MODES.DAILY_CAPACITY && parsed > MAX_ORDER_QUANTITY) {
    return {
      ok: false,
      code: "MAX_QUANTITY_EXCEEDED",
      message: `You can order up to ${MAX_ORDER_QUANTITY} of this item at a time.`,
      maxQuantity,
    };
  }

  if (maxQuantity <= 0) {
    return {
      ok: false,
      code: "OUT_OF_STOCK",
      message: "This item is currently out of stock.",
      maxQuantity,
    };
  }

  if (parsed > maxQuantity) {
    return {
      ok: false,
      code: "INSUFFICIENT_STOCK",
      message: `Only ${maxQuantity} available right now.`,
      maxQuantity,
    };
  }

  return {
    ok: true,
    quantity: parsed,
    maxQuantity,
  };
}

export function sortListingsByAvailability(listings) {
  if (!Array.isArray(listings)) return [];
  const rank = { available: 0, low: 1, out: 2 };
  rank.preview = 1;

  return listings
    .map((item, index) => {
      const availability = normalizeInventory(item).availability;
      return {
        item,
        index,
        rank: rank[availability] ?? 0,
      };
    })
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item);
}

export const AVAILABILITY_BADGE_PALETTE = {
  available: {
    light: { color: "#065f46", border: "#047857" },
    dark: { color: "#d1fae5", border: "rgba(110, 231, 183, 0.7)" },
  },
  low: {
    light: { color: "#92400e", border: "#b45309" },
    dark: { color: "#fef3c7", border: "rgba(252, 211, 77, 0.7)" },
  },
  out: {
    light: { color: "#9f1239", border: "#be123c" },
    dark: { color: "#ffe4e6", border: "rgba(251, 113, 133, 0.7)" },
  },
  preview: {
    light: { color: "#5b5f6b", border: "rgba(148, 163, 184, 0.72)" },
    dark: { color: "#e2e8f0", border: "rgba(148, 163, 184, 0.7)" },
  },
};

export function getAvailabilityBadgeStyle(availability, isLight) {
  const key =
    typeof availability === "string" ? availability : availability?.availability || null;
  const palette = key ? AVAILABILITY_BADGE_PALETTE[key] : null;
  if (!palette) return null;
  return isLight ? palette.light : palette.dark;
}
